import { attributes } from '../../core/ids-attributes';
import { customElement, scss } from '../../core/ids-decorators';
import {
  injectTemplate,
  stringToBool,
  stringToNumber,
  trimText
} from '../../utils/ids-string-utils/ids-string-utils';
import { calculateTextRenderWidth } from '../../utils/ids-object-utils/ids-object-utils';
import { QUALITATIVE_COLORS } from './ids-chart-colors';
import { patternData } from './ids-pattern-data';
import NiceScale from './ids-nice-scale';
import debounce from '../../utils/ids-debounce-utils/ids-debounce-utils';
import IdsChartLegendMixin from '../../mixins/ids-chart-legend-mixin/ids-chart-legend-mixin';
import IdsChartSelectionMixin, {
  ChartSelectionHandler
} from '../../mixins/ids-chart-selection-mixin/ids-chart-selection-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsElement from '../../core/ids-element';
import IdsDataSource from '../../core/ids-data-source';
import '../ids-tooltip/ids-tooltip';
import '../ids-empty-message/ids-empty-message';
import styles from './ids-axis-chart.scss';
import type IdsEmptyMessage from '../ids-empty-message/ids-empty-message';
import type IdsText from '../ids-text/ids-text';
import type IdsTooltip from '../ids-tooltip/ids-tooltip';
import { IdsDeferred } from '../../utils/ids-deferred-utils/ids-deferred-utils';
import IdsGlobal from '../ids-global/ids-global';
import { type IdsColorValue } from '../../utils/ids-color-utils/ids-color-utils';

export type IdsChartData = {
  abbreviatedName?: string,
  name?: string,
  value?: number,
  shortName?: string,
  color?: IdsColorValue,
  data?: Array<IdsChartData>,
  pattern?: string,
  patternColor?: IdsColorValue
};

export type IdsChartPointData = {
  area?: number,
  left?: number,
  right?: number,
  top: number,
  value: number,
  color?: IdsColorValue
};

export type IdsChartMarkerData = {
  /** The number of markers (dots ect) */
  markerCount: number,
  /** The min value in all groups */
  min: number,
  /** The max value in all groups */
  max: number,
  /** The max value in all groups */
  groupCount?: number,
  /** The scale calculations */
  scale: NiceScale,
  /** The y scale values */
  scaleValues?: Array<number>,
  /** The point data in the group */
  points?: Array<Array<IdsChartPointData>>,
  /** The location of the top of the grid */
  gridTop: number,
  /** The location of the bottom of the grid */
  gridBottom: number,
  /** The location of the left of the grid */
  gridLeft: number,
  /** The location of the right of the grid */
  gridRight: number
  /** The total within each group */
  groupTotals: Array<number>
  /** The zero lines top position */
  zeroLine: number
};

export type IdsChartDimensions = {
  left: number,
  right: number,
  top: number,
  bottom: number
};

export type SectionWidth = {
  width: number;
  left: number;
};

export type SectionHeight = {
  height: number;
  top: number;
};

const RESIZE_TIMEOUT = 200;
const DEFAULT_WIDTH = 700;
const DEFAULT_HEIGHT = 500;

const Base = IdsChartLegendMixin(
  IdsChartSelectionMixin(
    IdsLocaleMixin(
      IdsEventsMixin(
        IdsElement
      )
    )
  )
);

/**
 * IDS Axis Chart Component
 * @type {IdsAxisChart}
 * @inherits IdsElement
 * @mixes IdsChartSelectionMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsEventsMixin
 * @part container - the outside container element
 * @part chart - the svg outer element
 */
@customElement('ids-axis-chart')
@scss(styles)
export default class IdsAxisChart extends Base implements ChartSelectionHandler {
  initialized = false;

  dataLoaded = new IdsDeferred();

  svg?: SVGElement | null;

  canvas?: HTMLCanvasElement;

  emptyMessage?: IdsEmptyMessage | null;

  legend?: HTMLSlotElement | null;

  sectionWidths: SectionWidth[] = [];

  sectionWidth = NaN;

  sectionHeights: SectionHeight[] = [];

  sectionHeight = NaN;

  resizeToParentHeight = false;

  resizeToParentWidth = false;

  /** Reference to datasource API */
  datasource = new IdsDataSource();

  /** @returns {Array<string>} Drawer vetoable events */
  vetoableEventTypes = [
    'beforeselected',
    'beforedeselected'
  ];

  protected isGrouped = false;

  constructor() {
    super();

    // Setup the default values
    this.state = this.state || {};
    this.state.horizontal = false;
    this.state.yAxisFormatter = {
      notation: 'compact',
      compactDisplay: 'short'
    };
  }

  /**
   * On selectable change
   */
  onSelectableChange(): void {
    this.legendsClickable?.(this.selectable);
  }

  /**
   * Invoked each time the custom element is appended
   */
  connectedCallback(): void {
    super.connectedCallback?.();

    if (!this.hasAttribute(attributes.LEGEND_PLACEMENT)) {
      this.setAttribute(attributes.LEGEND_PLACEMENT, 'bottom');
    }

    this.svg = this.shadowRoot?.querySelector('svg');
    this.emptyMessage = this.querySelector('ids-empty-message') || this.shadowRoot?.querySelector('ids-empty-message');
    this.legend = this.shadowRoot?.querySelector('[name="legend"]');
    this.setAttribute('role', 'img');

    const themeLoaded = IdsGlobal.onThemeLoaded();
    if (themeLoaded) {
      this.init();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      Promise.all([
        IdsGlobal.onThemeLoaded().promise, // wait for container visible
        this.dataLoaded // wait for initial set of data
      ]).then(() => {
        this.init();
      });
    }
  }

  init(): void {
    if (this.getAttribute(attributes.WIDTH)) this.width = this.getAttribute(attributes.WIDTH) as string;
    if (this.getAttribute(attributes.HEIGHT)) this.height = this.getAttribute(attributes.HEIGHT) as string;

    this.initialized = true;
    this.#resetAxisLabelsText();
    this.#attachEventHandlers();
    this.#attachResizeObserver();
    this.redraw();
  }

  /**
   * Invoked each time the custom element is removed
   */
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.ANIMATED,
      attributes.ALIGN_X_LABELS,
      attributes.AXIS_LABEL_BOTTOM,
      attributes.AXIS_LABEL_END,
      attributes.AXIS_LABEL_MARGIN,
      attributes.AXIS_LABEL_START,
      attributes.AXIS_LABEL_TOP,
      attributes.DATA,
      attributes.HEIGHT,
      attributes.HORIZONTAL,
      attributes.MARGINS,
      attributes.TICKS,
      attributes.SHOW_HORIZONTAL_GRID_LINES,
      attributes.SHOW_VERTICAL_GRID_LINES,
      attributes.SHOW_TOOLTIP,
      attributes.ROTATE_NAME_LABELS,
      attributes.STACKED,
      attributes.TITLE,
      attributes.USE_LOG_SCALE,
      attributes.WIDTH
    ];
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    const horizontal = this.horizontal ? ' horizontal' : '';

    return `<div class="ids-chart-container" part="container">
      <svg class="ids-axis-chart${horizontal}" part="chart" width="${this.width}" height="${this.height}" xmlns="http://www.w3.org/2000/svg"}>
      </svg>
      <slot name="legend">
      </slot>
      <slot name="empty-message">
        <ids-empty-message icon="empty-no-data" hidden>
          <ids-text type="h2" font-size="20" label="true" slot="label">${this.localeAPI?.translate('NoData') || 'No data available'}</ids-text>
        </ids-empty-message>
      </slot>
      <slot name="tooltip">
        <ids-tooltip id="tooltip"></ids-tooltip>
      </slot>
    </div>`;
  }

  /**
   * Setup the Event Handling
   * @private
   */
  #attachEventHandlers(): void {
    this.onLocaleChange = () => {
      this.redraw();
      const textElem = this.shadowRoot?.querySelector<IdsText>('ids-empty-message ids-text');
      if (textElem) textElem.textContent = this.localeAPI?.translate('NoData');
    };

    this.onLanguageChange = () => {
      const textElem = this.shadowRoot?.querySelector<IdsText>('ids-empty-message ids-text');
      if (textElem) textElem.textContent = this.localeAPI?.translate('NoData');
    };
  }

  /** Max width for x-labels text */
  #xMaxTextWidth = 0;

  /** Max width for y-labels text */
  #yMaxTextWidth = 0;

  /** Holds the resize observer object */
  #resizeObserver?: ResizeObserver = undefined;

  /**
   * Attach the resize observer
   * @private
   */
  #attachResizeObserver(): void {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver ??= new ResizeObserver(debounce((entries: ResizeObserverEntry[]) => {
      const rect = entries[0].contentRect;
      const width = this.resizeToParentWidth ? rect.width : this.width;

      // height needs to be adjusted to (svg height - legend height)
      const height = this.resizeToParentHeight
        ? this.svg!.getBoundingClientRect().height
        : this.height;

      this.resize(width, height);
    }, RESIZE_TIMEOUT));

    this.#resizeObserver.observe(this.chartContainer!);
  }

  #detachResizeObserver(): void {
    const widthAttr = this.getAttribute(attributes.WIDTH);
    const heightAttr = this.getAttribute(attributes.HEIGHT);

    if (widthAttr !== 'inherit' && heightAttr !== 'inherit') {
      this.#resizeObserver?.disconnect();
      this.#resizeObserver = undefined;
    }
  }

  /**
   * Resize chart to given width height
   * @param {number} width parent width
   * @param {number} height parent height
   */
  resize(width: number, height: number): void {
    if (!this.initialized) return;

    if (width) {
      this.svg?.setAttribute(attributes.WIDTH, String(width));
    }

    if (height) {
      this.svg?.setAttribute(attributes.HEIGHT, String(height));
    }

    this.redraw();
  }

  /**
   * Redraw the chart
   * @private
   */
  redraw(): void {
    if (!this.initialized) return;

    if (this.data && this.data.length === 0 && this.initialized) {
      this.#showEmptyMessage();
      if (this.legend) this.legend.innerHTML = this.legendTemplate();
      return;
    }

    if (this.legend) this.legend.innerHTML = this.legendTemplate();
    this.#calculate();
    this.afterCalculateCallback();
    this.#addColorVariables();
    if (this.svg) this.svg.innerHTML = this.#axisTemplate();

    this.adjustLabels();
    this.#adjustRTL();

    this.legendsClickable?.(this.selectable);

    // Completed Event and Callback
    this.triggerEvent('rendered', this, {
      detail: {
        svg: this.svg,
        data: this.data,
        markerData: this.markerData
      }
    });

    this.afterConnectedCallback();
  }

  /**
   * Adjust RTL
   * @private
   */
  #adjustRTL(): void {
    if (!this.localeAPI?.isRTL()) return;

    const labels = {
      x: [...this.svg?.querySelectorAll('.labels.x-labels text') ?? []],
      y: [...this.svg?.querySelectorAll('.labels.y-labels text') ?? []]
    };

    // Adjust y-max text width
    const isRTL = this.localeAPI?.isRTL();
    // 12 pixels is the gap for ticks
    const extra = this.#yMaxTextWidth + this.margins.left - (isRTL ? 12 : 0);

    // X-labels
    let calcX: any = (x: any) => {
      let val = stringToNumber(x) - extra;
      val += this.horizontal ? (this.margins.leftInner + this.margins.rightInner) : 0;
      return val;
    };
    const newX = labels.x.map((label: any) => calcX(label.getAttribute('x'))).reverse();
    labels.x.forEach((label: any, i: number) => {
      if (!this.horizontal) {
        // For RTL with rotated labels, we need to adjust the text anchor and positioning
        if (this.rotateNameLabels !== 0) {
          const textAnchor = label.getAttribute('text-anchor') || '';

          // Preserve existing text anchor from the original label generation
          const rtlTransform = `rotate(${this.rotateNameLabels}, ${newX[i]}, ${label.getAttribute('y')})`;

          // For RTL, we need to flip the text anchor behavior for rotated labels
          if (this.rotateNameLabels < 0 && textAnchor === 'end') {
            label.setAttribute('text-anchor', 'start');
          } else if (this.rotateNameLabels > 0 && textAnchor === 'start') {
            label.setAttribute('text-anchor', 'end');
          }

          label.setAttribute('transform', rtlTransform);
        } else {
          label.setAttribute('transform', `rotate(${this.rotateNameLabels}, ${newX[i]}, ${label.getAttribute('y')})`);
        }
      }
      label.setAttribute('x', newX[i]);
      if (this.alignXLabels === 'middle') label.setAttribute('transform-origin', '0 -4px');
    });

    // Y-labels
    calcX = (x: any) => `-${stringToNumber(x) + extra}px`;
    labels.y.forEach((label: any) => {
      if (this.horizontal) {
        const trans = label.getAttribute('transform')?.split(',');
        if (trans?.length === 3) {
          const transX = stringToNumber(trans[1]);
          const transY = stringToNumber(trans[2]);
          label.setAttribute('transform-origin', `${transX}px ${transY}px`);
          label.setAttribute('transform', `rotate(${this.rotateNameLabels}) scale(-1, 1)`);
        } else {
          const calX = calcX(label.getAttribute('x'));
          const transStr = `scale(-1, 1) translate(${calX}, 0)`;
          label.style.setProperty('transform', transStr);
        }
      } else {
        const calX = calcX(label.getAttribute('x'));
        label.style.setProperty('--ids-axis-chart-ylabels-x', calX);
      }
    });
  }

  /** The marker data to use to draw the chart */
  markerData: IdsChartMarkerData = {
    markerCount: 0,
    groupCount: 0,
    min: 0,
    max: 0,
    scale: new NiceScale(this.yAxisMin, 0),
    gridTop: 0,
    gridBottom: 0,
    gridLeft: 0,
    gridRight: 0,
    groupTotals: [0],
    scaleValues: [0],
    zeroLine: 0
  };

  /**
   * Get the min/max points and calculate the scale
   * @private
   */
  #calculate(): void {
    let groupCount = 0;
    let markerCount = 0;
    this.markerData = {
      markerCount: 0,
      groupCount: 0,
      min: Infinity,
      max: -Infinity,
      scale: new NiceScale(this.yAxisMin, 0),
      gridTop: 0,
      gridBottom: 0,
      gridLeft: 0,
      gridRight: 0,
      groupTotals: [0],
      zeroLine: 0
    };

    // Get the Min and Max and Totals in one sequence
    this.data?.forEach((group: any, index: number) => {
      groupCount++;
      markerCount = 0;
      this.markerData.groupTotals[index] = 0;

      group.data?.forEach((data: any) => {
        if (data.value > this.markerData.max) {
          this.markerData.max = data.value;
        }
        this.markerData.groupTotals[index] += data.value;
        if (data.value < this.markerData.min) {
          this.markerData.min = data.value;
        }
        if (this.markerData.min === Infinity) {
          this.markerData.min = data.value;
        }
        markerCount++;
      });
      if (markerCount > this.markerData.markerCount) {
        this.markerData.markerCount = markerCount;
      }
      this.markerData.groupCount = groupCount;
    });

    // Handle log scale domain requirements
    if (this.useLogScale) {
      // Log scale requires positive domain values
      if (this.markerData.min <= 0) {
        this.markerData.min = 1;
      }
      if (this.markerData.max <= 0) {
        this.markerData.max = 10;
      }
    }

    // Edge case of one item or two items with same value
    if (this.markerData.min === this.markerData.max && this.markerData.min >= 0) {
      this.markerData.min = this.useLogScale ? 1 : 0;
    }

    if (this.markerData.min === this.markerData.max && this.markerData.min < 0) {
      this.markerData.max = 10;
      const tempScale: NiceScale = new NiceScale(
        this.markerData.min,
        this.stacked ? Math.max(...this.markerData.groupTotals) : this.markerData.max,
        { maxTicks: this.ticks }
      );
      this.markerData.max = Math.abs(Math.round((tempScale.tickSpacing || 0) / 3));
    }

    // Calculate scale values
    const groupMax = Math.max(...this.markerData.groupTotals);
    const scaleMax = this.stacked ? groupMax : this.markerData.max;
    const scaleMin = this.useLogScale ? Math.max(this.markerData.min, 1) : this.markerData.min;

    const scale: NiceScale = new NiceScale(
      scaleMin,
      scaleMax,
      { maxTicks: this.ticks }
    );

    this.markerData.scale = scale;
    this.markerData.scaleValues = [];

    if (this.useLogScale) {
      // Generate logarithmic scale values using powers of 10
      this.markerData.scaleValues = [];
      const logMin = Math.floor(Math.log10(scaleMin));
      const logMax = Math.ceil(Math.log10(scaleMax));

      for (let i = logMin; i <= logMax; i++) {
        this.markerData.scaleValues.push(10 ** i);
      }

      // Update scale bounds for log scale
      this.markerData.scale.niceMin = 10 ** logMin;
      this.markerData.scale.niceMax = 10 ** logMax;
    } else {
      for (let i = (scale.niceMin || 0); i <= (scale.niceMax); i += (Number(scale.tickSpacing))) {
        // If the absolute value is very small, convert to 0
        const value = Math.abs(i) < 1e-10 ? 0 : i;
        this.markerData.scaleValues.push(value);
      }
    }

    // Set max text width for labels
    this.xMaxTextWidth();
    this.yMaxTextWidth();

    this.markerData.gridTop = this.margins.top + this.textWidths.top;
    this.markerData.gridBottom = this.height - this.margins.bottom - this.textWidths.bottom;
    this.markerData.gridLeft = this.textWidths.left + this.margins.left
      + (this.margins.leftInner * 2) + this.margins.rightInner;
    this.markerData.gridRight = this.width - this.margins.right - this.textWidths.right;

    // Set the data points / locations
    if (this.horizontal) {
      this.#horizontalPoints();
      this.#sectionHeights();
    } else {
      this.#verticalPoints();
      this.#sectionWidths();
    }
  }

  /**
   * Set horizontal data points / locations
   * @private
   * @returns {void}
   */
  #horizontalPoints(): void {
    this.markerData.points = [];

    this.data?.forEach((dataPoints: IdsChartData) => {
      let top: number = this.textWidths.top + this.margins.top + this.margins.topInner;
      const points: Array<IdsChartPointData> = [];
      for (let index = 0; index < this.markerData.markerCount; index++) {
        let value = dataPoints.data?.[index]?.value || 0;

        // Handle zero/negative values for log scale
        if (this.useLogScale && value <= 0) {
          value = 1; // Use 1 as placeholder for zero/negative values
        }

        let cyPerc: number;

        if (this.useLogScale) {
          const logValue = Math.log10(Math.abs(value));
          const logMin = Math.log10(this.markerData.scale.niceMin || 1);
          const logMax = Math.log10(this.markerData.scale.niceMax);
          cyPerc = Math.max(0, Math.min(1, (logValue - logMin) / (logMax - logMin)));
        } else {
          cyPerc = ((value - (this.markerData.scale.niceMin || 0))
            / (this.markerData.scale.niceMax - this.markerData.scale.niceMin));
        }

        const cyZeroPerc = this.useLogScale ? 0 : ((this.markerData.zeroLine
            - (this.markerData.scale.niceMin < 0 ? this.markerData.scale.niceMin : 0))
          / (this.markerData.scale.niceMax - this.markerData.scale.niceMin));
        const cyWidth = ((cyPerc) * (this.markerData.gridRight - this.markerData.gridLeft));
        const cyZeroWidthDiff = ((cyZeroPerc) * (this.markerData.gridRight - this.markerData.gridLeft));

        points.push({
          top,
          right: this.useLogScale ? cyWidth : Math.abs(cyWidth - cyZeroWidthDiff),
          value: dataPoints.data?.[index]?.value || 0 // Keep original value
        });

        top += this.#namesLineGap();
      }
      this.markerData.points?.push(points as any);
    });
  }

  /**
   * Set vertical data points / locations
   * @private
   * @returns {void}
   */
  #verticalPoints(): void {
    this.markerData.points = [];
    const firstScaleValue = (this.markerData?.scaleValues?.at(0) || 0);
    const toDrawMinLine = firstScaleValue < 0 ? 0 : firstScaleValue;

    let zeroLine;
    if (this.useLogScale) {
      // For log scale, zero line is at the bottom
      zeroLine = this.markerData.gridBottom;
    } else {
      zeroLine = this.markerData.gridBottom
        - ((toDrawMinLine - this.markerData.scale.niceMin)
          / (this.markerData.scale.niceMax - this.markerData.scale.niceMin))
        * (this.markerData.gridBottom - this.markerData.gridTop);
    }
    this.markerData.zeroLine = zeroLine;

    this.data?.forEach((dataPoints: IdsChartData) => {
      let left: number = this.textWidths.left + this.margins.left + (this.margins.leftInner * 2);
      const points: Array<IdsChartPointData> = [];

      for (let index = 0; index < this.markerData.markerCount; index++) {
        let value = dataPoints.data?.[index]?.value || 0;

        // Handle zero/negative values for log scale
        if (this.useLogScale && value <= 0) {
          value = 1; // Use 1 as placeholder for zero/negative values
        }

        let cyPercArea: number;
        let cyPercAbsolute: number;

        if (this.useLogScale) {
          const logValue = Math.log10(Math.abs(value));
          const logMin = Math.log10(this.markerData.scale.niceMin || 1);
          const logMax = Math.log10(this.markerData.scale.niceMax);

          cyPercArea = Math.max(0, Math.min(1, (logValue - logMin) / (logMax - logMin)));
          cyPercAbsolute = cyPercArea;
        } else {
          cyPercArea = ((Number(value) - (this.markerData.scale.niceMin || 0))
            / (this.markerData.scale.niceMax - this.markerData.scale.niceMin));
          cyPercAbsolute = ((Math.abs(value) - (this.markerData.scale.niceMin || 0))
            / (this.markerData.scale.niceMax - this.markerData.scale.niceMin));
        }

        const cyHeightArea = (cyPercArea * (this.markerData.gridBottom - this.markerData.gridTop));
        const cyHeightAbsolute = (cyPercAbsolute * (this.markerData.gridBottom - this.markerData.gridTop));

        points.push({
          left,
          top: this.markerData.gridBottom - cyHeightAbsolute,
          area: this.markerData.gridBottom - cyHeightArea,
          value: dataPoints.data?.[index]?.value || 0 // Keep original value
        });

        left += this.#namesLineGap();
      }
      this.markerData.points?.push(points as any);
    });
  }

  /**
   * Set the widths of each category section (used in other places)
   * @private
   * @returns {void}
   */
  #sectionWidths(): void {
    this.sectionWidth = (this.markerData.gridRight - this.markerData.gridLeft) / this.markerData.markerCount;
    let left = this.textWidths.left + this.margins.left + (this.margins.leftInner * 2);
    this.sectionWidths = [];
    for (let index = 0; index < this.markerData.markerCount + 1; index++) {
      this.sectionWidths.push({ left, width: this.sectionWidth });
      left += this.sectionWidth;
    }
  }

  /**
   * Set the heights of each category section (used in other places)
   * @private
   * @returns {void}
   */
  #sectionHeights(): void {
    this.sectionHeight = (this.markerData.gridBottom - this.markerData.gridTop) / this.markerData.markerCount;
    let top = this.textWidths.top + this.margins.top + this.margins.topInner;
    if (this.isGrouped) top -= (this.markerData.groupCount || 0) % 2;
    this.sectionHeights = [];
    for (let index = 0; index < this.markerData.markerCount; index++) {
      this.sectionHeights.push({ top, height: this.sectionHeight });
      top += this.sectionHeight;
    }
  }

  /**
   * Add colors in a style sheet to the root so the variables can be used
   * @private
   */
  #addColorVariables(): void {
    if (!this.shadowRoot) {
      return;
    }

    let colorSheet = '';
    this.data?.forEach((group: IdsChartData, index: number) => {
      const data = (group as any);
      let color = data.patternColor;
      if (!color && data.color && data.color.substr(0, 1) === '#') {
        color = data.color;
      }
      if (!color && data.color && data.color.substr(0, 1) !== '#' && data.color.substr(0, 11) !== '--ids-color') {
        color = `var(--ids-color-${data.color})`;
      }
      if (!color && data.color && data.color.substr(0, 1) !== '#' && data.color.substr(0, 11) === '--ids-color') {
        color = `var(${data.color})`;
      }
      if (!color) {
        color = `var(${this.colors[index]})`;
      }
      colorSheet += `--ids-chart-color-${index + 1}: ${color} !important;`;
    });

    // Create a separate stylesheet for dynamic color variables
    const variablesSheet = new CSSStyleSheet();
    variablesSheet.replaceSync(`:host { ${colorSheet} }`);

    // Add to existing adoptedStyleSheets
    const currentSheets = this.shadowRoot.adoptedStyleSheets || [];
    this.shadowRoot.adoptedStyleSheets = [...currentSheets, variablesSheet];
  }

  /**
   * Return the insider part of the SVG
   * @private
   * @returns {string} The SVG markup
   */
  #axisTemplate(): string {
    const template = `
      <defs>
        ${this.#patterns()}
      </defs>
      <g class="grid vertical-lines${!this.showVerticalGridLines ? ' hidden' : ''}">
        ${this.#verticalLines()}
      </g>
      <g class="grid horizontal-lines${!this.showHorizontalGridLines ? ' hidden' : ''}">
        ${this.#horizontalLines()}
      </g>
      ${this.chartTemplate()}
      <g class="labels x-labels">
        ${this.#xLabels()}
      </g>
      <g class="labels y-labels">
        ${this.#yLabels()}
      </g>
      <g class="labels axis-labels">
        ${this.#axisLabels()}
      </g>`;
    return template;
  }

  /**
   * Overridable method to draw the markers
   * @returns {string} The SVG Marker Markup
   */
  chartTemplate(): string {
    return '';
  }

  /**
   * Child Chart elements that get tooltips
   * @private
   * @returns {Array<SVGElement>} The elements
   */
  tooltipElements(): Array<SVGElement> {
    return [];
  }

  /**
   * Overridable method to draw to get the tooltip template
   * @returns {string} The tooltip template
   */
  tooltipTemplate(): string {
    // eslint-disable-next-line no-template-curly-in-string
    return '<b>${label}</b> ${value}';
  }

  /**
   * Setup handlers on tooltip elements
   */
  attachTooltipEvents(): void {
    if (!this.showTooltip) return;

    // Need one event per bar due to the nature of the events for tooltip
    this.tooltipElements().forEach((element: SVGElement) => {
      this.onEvent('hoverend', element, async () => {
        const tooltip = this.container?.querySelector<IdsTooltip>('ids-tooltip');
        if (tooltip) {
          tooltip.innerHTML = this.#tooltipContent(element);
          tooltip.target = element;
          tooltip.placement = 'top';
          tooltip.visible = true;
        }
      });

      // Attach Click Event for Mobile
      this.onEvent('click.tooltip', element, async () => {
        const tooltip = this.container?.querySelector<IdsTooltip>('ids-tooltip');
        if (tooltip) {
          tooltip.innerHTML = this.#tooltipContent(element);
          tooltip.target = element;
          tooltip.placement = 'top';
          tooltip.visible = true;
        }
      });
    });

    this.onEvent('touchmove.tooltip', this, () => {
      const tooltip = this.container?.querySelector<IdsTooltip>('ids-tooltip');
      if (tooltip) {
        tooltip.visible = false;
      }
    }, { passive: true });
  }

  /**
   * Return the data for a tooltip accessible by index
   * @param {number} index the data groupIndex
   * @param {number} groupIndex the data index
   * @returns {Array<string>} The elements
   */
  tooltipData(index: number, groupIndex = 0) {
    const data = (this.data as any)[groupIndex]?.data;

    return {
      label: data[index]?.name || (this.data as any)[0].data[index].name,
      value: data[index]?.value || 0,
      tooltip: data[index]?.tooltip
    };
  }

  /**
   * Return the tooltip content
   * @param {SVGElement} elem The svg element we will inspect for content
   * @private
   * @returns {string} The tooltip content
   */
  #tooltipContent(elem: SVGElement): string {
    const group = Number(elem.getAttribute('group-index'));
    const index = Number(elem.getAttribute('index'));
    const data = this.tooltipData(index, group);

    if (data.tooltip) {
      // eslint-disable-next-line no-template-curly-in-string
      return data.tooltip.replace('${value}', data.value).replace('${label}', data.label);
    }

    if (this.stacked || this.isGrouped) {
      let html = `<div class="tooltip-center"><b>${data.label}</b></div><div class="tooltip chart-legend">`;
      for (let i = 0; i < this.data.length; i++) {
        const dataGroup = this.data[i];
        const label = dataGroup.name;
        const value = (this.data as any)[i].data[index]?.value;
        const colorClass = dataGroup.pattern ? '' : ` color-${i + 1}`;
        const patternSvg = dataGroup.pattern ? `<svg width="12" height="12" xmlns="http://www.w3.org/2000/svg"><rect width="12" height="12" fill="url(#${dataGroup.pattern})"></rect></svg>` : '';

        if (label && (value || value === 0)) {
          html += `<div class="tooltip-row">
            <div class="swatch${colorClass}">${patternSvg}</div>
            <span>${this.data[i].name}</span>
            <b>${(this.data as any)[i].data[index].value}</b></div>`;
        }
      }
      return `${html}</div>`;
    }
    return injectTemplate(this.tooltipTemplate(), data);
  }

  /**
   * Holds the axis labels text object
   * @private
   */
  #axisLabelsText?: {
    bottom: string,
    end: string,
    start: string,
    top: string
  };

  /**
   * Reset the axis labels
   * @private
   * @returns {void}
   */
  #resetAxisLabelsText(): void {
    this.#axisLabelsText = {
      bottom: '',
      end: '',
      start: '',
      top: ''
    };
  }

  /**
   * Set the axis labels
   * @private
   * @param {'bottom'|'end'|'start'|'top'} opt The option
   * @returns {void}
   */
  #setAxisLabels(opt: 'bottom' | 'end' | 'start' | 'top'): void {
    if (this.#axisLabelsText) {
      const current = this.#axisLabelsText[opt];
      const labels = {
        bottom: this.axisLabelBottom,
        end: this.axisLabelEnd,
        start: this.axisLabelStart,
        top: this.axisLabelTop
      };

      if (typeof current !== 'undefined' && current !== labels[opt]) {
        this.#axisLabelsText[opt] = labels[opt] || '';
        if (this.initialized) this.redraw();
      }
    }
  }

  /**
   * Return the axis label for the svg
   * @private
   * @returns {string} The axis label markup
   */
  #axisLabels(): string {
    let html = '';
    if (this.#axisLabelsText) {
      // Size
      const gap = 12;
      const inline = { start: gap, mid: this.width / 2, end: this.width - gap };
      const block = { start: gap, mid: this.height / 2, end: this.height - gap };

      // Position
      const isRTL = this.localeAPI?.isRTL();
      const scale = isRTL ? ' scale(-1, 1)' : '';
      const transform = {
        top: `translate(${inline.mid}, ${block.start})${scale}`,
        bottom: `translate(${inline.mid}, ${block.end})${scale}`,
        start: `translate(${inline.start}, ${block.mid}) rotate(-90)${scale}`,
        end: `translate(${inline.end}, ${block.mid}) rotate(90)${scale}`
      };

      // HTML
      ['top', 'bottom', 'start', 'end'].forEach((type: string) => {
        if ((this.#axisLabelsText as any)[type]) {
          html += `<text
          class="axis-label-${type}"
          transform="${(transform as any)[type]}"
        >${(this.#axisLabelsText as any)[type]}</text>`;
        }
      });
    }
    return html;
  }

  /**
   * Return the horizontal line data for the svg
   * @private
   * @returns {string} The horizontal line markup
   */
  #horizontalLines(): string {
    let lineHtml = '';
    let top = this.margins.top + this.margins.topInner;
    const start = this.textWidths.left + this.margins.left + this.margins.leftInner;
    const end = this.width - this.margins.right;

    // Max number of lines
    const { markerCount, scaleValues } = this.markerData;
    const max = this.horizontal ? (markerCount + 1) : (scaleValues?.length || 0);

    // Find the index of the zero value
    let zeroIndex = 0;
    for (let i = 0; i < scaleValues!.length; i++) {
      if (scaleValues![i] === 0) {
        zeroIndex = i;
        break;
      }
    }
    zeroIndex = max - zeroIndex - 1;

    for (let i = 0; i < max; i++) {
      const isZeroClass = (zeroIndex === i) ? 'is-minimum' : '';
      lineHtml += `<line class="${isZeroClass}" x1="${start}" x2="${end}" y1="${top}" y2="${top}"></line>`;
      top += this.horizontal ? this.#namesLineGap() : this.#valuesLineGap();
    }

    return lineHtml;
  }

  /**
   * Return the vertical line data for the svg
   * @private
   * @returns {string} The vertical line markup
   */
  #verticalLines(): string {
    let lineHtml = '';
    let left = this.textWidths.left + this.margins.left;
    left += this.margins.leftInner * (this.horizontal ? 1 : 2);
    const top = this.margins.top;
    const bottom = this.height - this.margins.bottom - this.textWidths.bottom;

    // Max number of lines
    const { markerCount, scaleValues } = this.markerData;
    const max = this.horizontal ? (scaleValues?.length || 0) : markerCount;

    // Find the index of the zero value
    let minimumIndex = -1;
    for (let i = 0; i < scaleValues!.length; i++) {
      if (scaleValues![i] === 0) {
        minimumIndex = i;
        break;
      }
    }

    minimumIndex = minimumIndex !== -1 ? minimumIndex : 0;

    for (let i = 0; i < max; i++) {
      const isZeroClass = (minimumIndex === i) ? 'is-minimum' : '';
      if (this.horizontal && minimumIndex === i) this.markerData.zeroLine = left;
      lineHtml += `<line ${isZeroClass ? `class="${isZeroClass}" ` : ''}x1="${left}" x2="${left}" y1="${top}" y2="${bottom}"></line>`;
      left += this.horizontal ? this.#valuesLineGap() : this.#namesLineGap();
    }

    return lineHtml;
  }

  /**
   * Format the value for the x label in a variety of ways
   * @param {string|Function} value The value to format value
   * @returns {string} The formatted value
   * @private
   */
  #formatXLabel(value: string | ((value: unknown, data: Array<IdsChartData>, api: this) => string)) {
    if (!this.xAxisFormatter) return value;

    if (typeof this.xAxisFormatter === 'function') {
      return this.xAxisFormatter(value, this.data, this);
    }
    return '';
  }

  /**
   * Format the value for the y label in a variety of ways
   * @param {string|Function} value The value to format value
   * @returns {string} The formatted value
   * @private
   */
  formatYLabel(value: string | ((value: unknown, data: Array<IdsChartData>, api: this) => string)) {
    if (!this.yAxisFormatter) {
      return this.useLogScale ? this.formatLogScaleValue(value as string) : value;
    }

    if (typeof this.yAxisFormatter === 'function') {
      return this.yAxisFormatter(value, this.data, this);
    }

    const numValue = parseFloat(value as string);
    if (!Number.isNaN(numValue) && numValue % 1 !== 0) {
      const formatterOptions = { ...this.yAxisFormatter, maximumFractionDigits: 3, minimumFractionDigits: 1 };
      return new Intl.NumberFormat(this.locale || 'en', formatterOptions).format(numValue);
    }

    return new Intl.NumberFormat(this.locale || 'en', this.yAxisFormatter).format(value as any);
  }

  /**
   * Format log scale values with appropriate units
   * @param {string} value The value to format
   * @returns {string} The formatted value
   * @private
   */
  private formatLogScaleValue(value: string): string {
    const numValue = parseFloat(value);
    if (Number.isNaN(numValue) || numValue <= 0) return value;

    if (numValue >= 1000000) return `${(numValue / 1000000).toFixed(0)}M`;
    if (numValue >= 1000) return numValue.toLocaleString();
    return numValue.toString();
  }

  #nameLabels(): string {
    let labelHtml = '';
    let left = 0;
    let top = 0;
    const gap = this.#namesLineGap();
    const textLengthThreshold = 15;

    if (this.horizontal) {
      // 3 is the half height of the text - could figure this out based on font size?
      const textSize = 3;
      left = this.textWidths.left + this.margins.left;
      top = this.margins.top + this.margins.topInner + textSize + (gap / 2);
      if (this.axisLabelTop) top += this.axisLabelMargin;

      for (let index = 0; index < this.markerData.markerCount; index++) {
        const transform = this.rotateNameLabels !== 0
          ? ` transform="rotate(${this.rotateNameLabels}, ${left}, ${top})"` : '';
        const value = this.#formatXLabel((this.data as any)[0]?.data[index]?.name);
        labelHtml += `<text x="${left}" y="${top}"${transform}>${value}</text>`;
        top += gap;
      }
    } else {
      left = this.textWidths.left + this.margins.left + (this.margins.leftInner * 2);
      top = this.height - this.margins.top - this.margins.bottom + this.margins.bottomInner;
      if (this.axisLabelTop) top += this.axisLabelMargin;

      for (let index = 0; index < this.markerData.markerCount; index++) {
        const isLast = index + 1 === this.markerData.markerCount;
        const value = this.#formatXLabel((this.data as any)[0]?.data[index]?.name);
        let transform;
        let textAnchor = '';

        if (this.alignXLabels === 'middle') {
          const x = left + (this.sectionWidths[index].width / 2);

          if (this.rotateNameLabels !== 0) {
            // For rotated labels, adjust text anchor based on rotation direction
            let anchorValue = 'middle';
            if (this.rotateNameLabels < 0) {
              anchorValue = 'end';
            } else if (this.rotateNameLabels > 0) {
              anchorValue = 'start';
            }
            textAnchor = ` text-anchor="${anchorValue}"`;
            transform = ` transform="rotate(${this.rotateNameLabels}, ${x}, ${top})"${textAnchor}`;
          } else {
            transform = ' alignment-baseline="middle" text-anchor="middle"';
          }

          labelHtml += `<text x="${x}" y="${top}"${transform}>${value}</text>`;
        } else {
          // For non-middle alignment
          let xPos = left;

          if (this.rotateNameLabels !== 0) {
            // Adjust positioning for rotated labels to prevent clipping
            if (index === 0 && this.rotateNameLabels < 0) {
              // First label with negative rotation - move right to prevent left clipping
              const angle = Math.abs(this.rotateNameLabels);
              const radians = (angle * Math.PI) / 180;
              const textWidth = this.#getTextWidth(value);
              const adjustment = textWidth * Math.cos(radians) * 0.5;
              xPos += adjustment;
            } else if (isLast && this.rotateNameLabels > 0) {
              // Last label with positive rotation - move left to prevent right clipping
              const angle = Math.abs(this.rotateNameLabels);
              const radians = (angle * Math.PI) / 180;
              const textWidth = this.#getTextWidth(value);
              const adjustment = textWidth * Math.cos(radians) * 0.5;
              xPos -= adjustment;
            }

            // Set text anchor based on rotation direction - this will be adjusted in RTL
            const anchorValue = this.rotateNameLabels < 0 ? 'end' : 'start';
            textAnchor = ` text-anchor="${anchorValue}"`;
            transform = ` transform="rotate(${this.rotateNameLabels}, ${xPos}, ${top})"${textAnchor}`;
          } else {
            const defaultTransform = isLast ? ' text-anchor="end"' : '';
            transform = defaultTransform;
          }

          labelHtml += `<text x="${xPos}" y="${top}"${transform}>${isLast && !this.rotateNameLabels ? trimText(value, textLengthThreshold) : value}</text>`;
        }
        left += this.alignXLabels === 'middle' ? this.sectionWidths[index].width : gap;
      }
    }
    return labelHtml;
  }

  /**
   * Return the value labels data
   * @private
   * @returns {string} The value labels markup
   */
  #valueLabels(): string {
    let lineHtml = '';
    let left = 0;
    let top = 0;

    if (this.horizontal) {
      left = this.textWidths.left + this.margins.left + this.margins.leftInner;
      top = this.height - this.margins.top - this.margins.bottom + this.margins.bottomInner;
      if (this.axisLabelTop) top += this.axisLabelMargin;

      this.markerData.scaleValues?.slice().forEach((value: any) => {
        lineHtml += `<text x="${left}" y="${top}">${this.formatYLabel(value)}</text>`;
        left += this.#valuesLineGap();
      });
    } else {
      // 3 is the half height of the text - could figure this out based on font size?
      const textSize = 3;
      left = this.textWidths.left + this.margins.left;
      top = this.margins.top + textSize;

      this.markerData.scaleValues?.slice().reverse().forEach((value: any) => {
        lineHtml += `<text x="${left}" y="${top}">${this.formatYLabel(value)}</text>`;
        top += this.#valuesLineGap();
      });
    }
    return lineHtml;
  }

  #namesLineGap() {
    if (this.horizontal) {
      const { gridTop, gridBottom, markerCount } = this.markerData;
      return (gridBottom - gridTop) / markerCount;
    }
    const left = this.textWidths.left + this.margins.left + this.margins.leftInner;
    const width = this.width - this.margins.right - (this.margins.rightInner * 2);
    return ((width - left) / (this.markerData.markerCount - 1));
  }

  #valuesLineGap() {
    const len = this.markerData.scaleValues?.length || 0;
    let v = 0;
    if (this.horizontal) {
      v += this.margins.left + this.margins.right;
      v += this.textWidths.left + this.textWidths.right;
      v += (this.margins.leftInner * 2) + this.margins.rightInner;
      v = this.width - v;
    } else {
      v += this.margins.top + this.margins.bottom;
      v += this.textWidths.top + this.textWidths.bottom;
      v = this.height - v;
    }
    return (v / (len - 1));
  }

  /**
   * Return the x label data for the svg
   * @private
   * @returns {string} The x label markup
   */
  #xLabels(): string {
    return this.horizontal ? this.#valueLabels() : this.#nameLabels();
  }

  /**
   * Return the y label data for the svg
   * @private
   * @returns {string} The y label markup
   */
  #yLabels(): string {
    return this.horizontal ? this.#nameLabels() : this.#valueLabels();
  }

  /**
   * Show an empty message with settings configuration
   * @private
   */
  #showEmptyMessage() {
    this.svg?.classList.add('hidden');
    this.emptyMessage?.style.setProperty('height', `${this.height}px`);
    this.emptyMessage?.removeAttribute('hidden');
  }

  /**
   * Hide the empty message
   * @private
   */
  #hideEmptyMessage() {
    this.svg?.classList.remove('hidden');
    this.emptyMessage?.style.setProperty('height', '');
    this.emptyMessage?.setAttribute('hidden', '');
  }

  /**
   * Return the def markup for svg patterns
   * @private
   * @returns {string} The string with all the patterns being used
   */
  #patterns(): string {
    let patternHtml = '';
    this.data?.forEach((group: any, i: number) => {
      let pattern = patternData[group.pattern];
      if (pattern) {
        const color = `${this.color(i)}` || '#000000';
        pattern = pattern.replace('fill="#000000"', `fill="${color}"`);
        patternHtml += pattern;
      }
    });
    return patternHtml;
  }

  /**
   * Set the chart title element for accessibility
   * @param {string} value The title value
   */
  set title(value: string) {
    if (value) {
      this.setAttribute('aria-label', value);
      this.removeAttribute('title');
    }
  }

  /**
   * Get the chart title
   * @returns {string} the chart title
   */
  get title(): string {
    return this.getAttribute('aria-label') || '';
  }

  /**
   * The width of the chart (in pixels) or 'inherit' from the parent
   * @param {number|string|'inherit'|'fluid'} value The height value
   */
  set height(value: number | string | 'inherit' | 'fluid' | null) {
    if (value === 'inherit' || value === 'fluid') {
      this.setAttribute(attributes.HEIGHT, String(value));
      this.chartContainer?.classList.add('full-height');
      this.chartContainer?.style.removeProperty('height');
      this.resizeToParentHeight = true;
      this.#attachResizeObserver();
      return;
    }

    if (!Number.isNaN(Number(value))) {
      const height = Number(value);
      this.setAttribute(attributes.HEIGHT, String(height));
      this.svg?.setAttribute(attributes.HEIGHT, String(height));
      this.redraw();
    }

    this.chartContainer?.classList.remove('full-height');
    this.resizeToParentHeight = false;
    this.#detachResizeObserver();
  }

  /**
   * Get the chart height
   * @returns {number} the chart height in pixels
   */
  get height(): number {
    const attrValue = this.getAttribute(attributes.HEIGHT);
    const pixelValue = stringToNumber(attrValue);

    if ((attrValue === 'inherit' || attrValue === 'fluid') && this.svg) {
      return this.svg.getBoundingClientRect().height;
    }

    if (!Number.isNaN(pixelValue)) {
      return pixelValue;
    }

    return DEFAULT_HEIGHT;
  }

  /**
   * Flips the orientation to horizontal
   * supported `Axis` and `Bar` type charts
   * @param {boolean | string} value If true will set `horizontal` attribute
   */
  set horizontal(value: boolean | string) {
    if (!(/^ids-(axis|bar)-chart$/gi.test(this.nodeName))) return;

    const val = stringToBool(value);
    if (val !== this.state.horizontal) {
      this.state.horizontal = val;
      if (val) {
        this.setAttribute(attributes.HORIZONTAL, '');
        this.svg?.classList.add(attributes.HORIZONTAL);
      } else {
        this.removeAttribute(attributes.HORIZONTAL);
        this.svg?.classList.remove(attributes.HORIZONTAL);
      }
      this.redraw();
    }
  }

  /**
   * Get the horizontal orientation state
   * @returns {boolean | string} true if chart is horizontal
   */
  get horizontal(): boolean | string {
    return this.state.horizontal;
  }

  /**
   * The width of the chart (in pixels) or 'inherit' from the parent
   * @param {number|string|'inherit'|'fluid'} value The width value
   */
  set width(value: number | string | 'inherit' | 'fluid' | null) {
    if (value === 'inherit' || value === 'fluid') {
      this.setAttribute(attributes.WIDTH, String(value));
      this.chartContainer?.classList.add('full-width');
      this.chartContainer?.style.removeProperty('width');
      this.resizeToParentWidth = true;
      this.#attachResizeObserver();
      return;
    }

    if (!Number.isNaN(Number(value))) {
      const width = Number(value);
      this.setAttribute(attributes.WIDTH, String(width));
      this.svg?.setAttribute(attributes.WIDTH, String(width));
      this.#setContainerWidth(Number(width));
      this.redraw();
    }

    this.chartContainer?.classList.remove('full-width');
    this.resizeToParentWidth = false;
    this.#detachResizeObserver();
  }

  /**
   * Get the chart width
   * @returns {number} the chart width in pixels
   */
  get width(): number {
    const attrValue = this.getAttribute(attributes.WIDTH);
    const pixelValue = stringToNumber(attrValue);

    if ((attrValue === 'inherit' || attrValue === 'fluid') && this.svg) {
      return this.chartContainer!.offsetWidth;
    }

    if (!Number.isNaN(pixelValue)) {
      return pixelValue;
    }

    return DEFAULT_WIDTH;
  }

  /**
   * Get the chart container element
   * @returns {HTMLElement | null} the chart container element
   */
  get chartContainer(): HTMLElement | null {
    return this.shadowRoot?.querySelector('.ids-chart-container') || null;
  }

  /**
   * Set the container width (for correct legend and sizing)
   * @param {number} value The width value
   */
  #setContainerWidth(value: number) {
    this.chartContainer?.style.setProperty('width', `${value}px`);
  }

  /**
   * Get the max width to render names axis
   * @private
   * @returns {number} Calculated width
   */
  #maxTextWidthForNames(): number {
    let maxWidth = 0;
    for (let index = 0; index < this.markerData.markerCount; index++) {
      const v = this.#formatXLabel((this.data as any)[0]?.data[index]?.name);
      const w = calculateTextRenderWidth(this, v);
      if (w > maxWidth) maxWidth = w;
    }
    return maxWidth;
  }

  /**
   * Get the max width to render values axis
   * @private
   * @returns {number} Calculated width
   */
  #maxTextWidthForValues(): number {
    let maxWidth = 0;
    this.markerData.scaleValues?.slice().forEach((value: any) => {
      const v = this.formatYLabel(value);
      const w = calculateTextRenderWidth(this, v);
      if (w > maxWidth) maxWidth = w;
    });
    return maxWidth;
  }

  /**
   * Set the max width to render the x-axis
   * @private
   * @returns {void}
   */
  xMaxTextWidth(): void {
    this.#xMaxTextWidth = this.horizontal
      ? this.#maxTextWidthForValues()
      : this.#maxTextWidthForNames();
  }

  /**
   * Set the max width to render the y-axis
   * @private
   * @returns {void}
   */
  yMaxTextWidth(): void {
    this.#yMaxTextWidth = this.horizontal
      ? this.#maxTextWidthForNames()
      : this.#maxTextWidthForValues();
  }

  /**
   * Set the left, right, top, bottom margins
   * @param {object} value The margin values
   */
  set margins(value) {
    this.state.margins = value;
    this.redraw();
  }

  /**
   * Get the chart margins
   * @returns {object} the margin values for left, right, top, bottom
   */
  get margins() {
    return this.state?.margins || {
      left: this.axisLabelsMargin.start + this.leftRotateMargin,
      right: (this.legendPlacement === 'right' ? 150 : 4) + this.axisLabelsMargin.end + this.rightRotateMargin,
      top: 16 + this.axisLabelsMargin.top,
      bottom: 12 + this.axisLabelsMargin.bottom + this.bottomRotateMargin,
      leftInner: 8,
      rightInner: 8,
      topInner: 0,
      bottomInner: 12
    };
  }

  /**
   * Get approximate text width for a given string
   * @param {string} text The text to measure
   * @returns {number} The approximate width
   * @private
   */
  #getTextWidth(text: string): number {
    // Use a rough approximation - could be improved with actual text measurement
    return text.length * 8; // Approximate character width
  }

  /**
   * Get left rotate margin for rotated labels
   * @returns {number} the calculated left margin
   */
  get leftRotateMargin(): number {
    if (!this.rotateNameLabels || this.horizontal) return 0;
    const angle = Math.abs(this.rotateNameLabels);
    const maxTextWidth = this.#xMaxTextWidth;

    // For negative angles (clockwise rotation), calculate left margin needed
    if (this.rotateNameLabels < 0) {
      const radians = (angle * Math.PI) / 180;
      const widthProjection = maxTextWidth * Math.cos(radians);
      return Math.max(0, widthProjection - 20);
    }
    return 0;
  }

  /**
   * Get right rotate margin for rotated labels
   * @returns {number} the calculated right margin
   */
  get rightRotateMargin(): number {
    if (!this.rotateNameLabels || this.horizontal) return 0;
    const angle = Math.abs(this.rotateNameLabels);
    const maxTextWidth = this.#xMaxTextWidth;

    // For positive angles (counter-clockwise rotation), calculate right margin needed
    if (this.rotateNameLabels > 0) {
      const radians = (angle * Math.PI) / 180;
      const widthProjection = maxTextWidth * Math.cos(radians);
      return Math.max(0, widthProjection - 20);
    }
    return 0;
  }

  /**
   * Get bottom rotate margin for rotated labels
   * @returns {number} the calculated bottom margin
   */
  get bottomRotateMargin(): number {
    if (!this.rotateNameLabels || this.horizontal) return 0;
    const angle = Math.abs(this.rotateNameLabels);
    const maxTextWidth = this.#xMaxTextWidth;

    // Calculate the height projection of rotated text
    // For negative angles (clockwise rotation), we need more bottom margin
    const radians = (angle * Math.PI) / 180;
    const heightProjection = maxTextWidth * Math.sin(radians);

    // Add some padding to prevent overlap
    return Math.max(0, heightProjection - 12) + 8;
  }

  /**
   * Get axis labels margin values
   * @returns {{ bottom: number; end: number; start: number; top: number; }} the margin values for each axis label
   */
  get axisLabelsMargin(): { bottom: number; end: number; start: number; top: number; } {
    const margin = this.axisLabelMargin;
    return {
      bottom: this.axisLabelBottom ? margin : 0,
      end: this.axisLabelEnd ? margin : 0,
      start: this.axisLabelStart ? margin : 0,
      top: this.axisLabelTop ? margin : 0
    };
  }

  /**
   * Set the width the text labels/axes take up on each side.
   * @param {object} value The margin values
   */
  set textWidths(value) {
    this.state.textWidths = value;
    this.redraw();
  }

  /**
   * Get text widths for chart dimensions
   * @returns {IdsChartDimensions} the text width dimensions
   */
  get textWidths(): IdsChartDimensions {
    return this.state.textWidths || {
      left: this.legendPlacement === 'left' ? 34 : (this.#yMaxTextWidth || 4), // TODO: Calculate this
      right: 0,
      top: 0,
      bottom: 24
    };
  }

  /**
   * Set the data array of the chart
   * @param {Array<unknown>} value The array to use
   */
  set data(value: Array<IdsChartData>) {
    if (value) {
      this.#hideEmptyMessage();
      this.datasource.data = value as any;
      this.dataLoaded.resolve();
      this.redraw();
      this.reanimate();
      return;
    }
    this.datasource.data = [];
  }

  /**
   * Get the chart data
   * @returns {Array<IdsChartData>} the chart data array
   */
  get data(): Array<IdsChartData> {
    return this?.datasource?.data || [];
  }

  /**
   * Set the minimum value on the y axis
   * @param {number} value The value to use
   */
  set yAxisMin(value: number) {
    this.setAttribute(attributes.Y_AXIS_MIN, String(value));
    this.redraw();
  }

  /**
   * Get the minimum value on the y axis
   * @returns {number} the y axis minimum value
   */
  get yAxisMin(): number {
    return parseInt(this.getAttribute(attributes.Y_AXIS_MIN) ?? '') || 0;
  }

  /**
   * Set the number of ticks to show
   * @param {number} value The value to use
   */
  set ticks(value: number | undefined) {
    this.setAttribute(attributes.TICKS, String(value));
    this.redraw();
  }

  /**
   * Get the number of ticks to show
   * @returns {number | undefined} the number of ticks
   */
  get ticks(): number | undefined {
    const value = this.getAttribute(attributes.TICKS);
    if (value) return Number(value);
    return undefined;
  }

  /**
   * Disable Tooltips
   * @param {boolean | string} value True or false to show the grid lines
   */
  set showTooltip(value: boolean | string) {
    this.setAttribute(attributes.SHOW_TOOLTIP, String(value));
  }

  /**
   * Get the show tooltip state
   * @returns {boolean | string} true if tooltips are enabled
   */
  get showTooltip(): boolean | string {
    const value = this.getAttribute(attributes.SHOW_TOOLTIP);
    return value !== null ? stringToBool(value) : true;
  }

  /**
   * Show the vertical axis grid lines
   * @param {boolean} value True or false to show the grid lines
   */
  set showVerticalGridLines(value: boolean) {
    this.setAttribute(attributes.SHOW_VERTICAL_GRID_LINES, String(value));
    this.redraw();
  }

  /**
   * Get the show vertical grid lines state
   * @returns {boolean} true if vertical grid lines are shown
   */
  get showVerticalGridLines(): boolean {
    const value = this.getAttribute(attributes.SHOW_VERTICAL_GRID_LINES);
    return value !== null ? stringToBool(value) : !!this.horizontal;
  }

  /**
   * Show the horizontal axis grid lines
   * @param {boolean} value True or false to show the grid lines
   */
  set showHorizontalGridLines(value: boolean) {
    this.setAttribute(attributes.SHOW_HORIZONTAL_GRID_LINES, String(value));
    this.redraw();
  }

  /**
   * Get the show horizontal grid lines state
   * @returns {boolean} true if horizontal grid lines are shown
   */
  get showHorizontalGridLines(): boolean {
    const value = this.getAttribute(attributes.SHOW_HORIZONTAL_GRID_LINES);
    return value !== null ? stringToBool(value) : !this.horizontal;
  }

  /**
   * Get the colors series being used in this chart
   * @returns {Array<string>} the colors being used on this instance
   */
  get colors(): Array<string> {
    return QUALITATIVE_COLORS;
  }

  /**
   * Get the color to use based on the index for sequential and custom colors
   * @param {number} index The current index
   * @returns {string} The color to use
   * @private
   */
  color(index: number): string {
    if (this.data[index].patternColor) {
      return (this.data[index].patternColor as any);
    }
    return `var(${this.data[index].color ? `color-${index + 1}` : this.colors[index]})`;
  }

  /**
   * Set the format on the x axis items
   * @param {Function} value A string with the formatting routine or a function for more customization.
   */
  set xAxisFormatter(value: any) {
    this.state.xAxisFormatter = value;
    this.redraw();
  }

  /**
   * Get the x axis formatter
   * @returns {any} the x axis formatter function or configuration
   */
  get xAxisFormatter(): any {
    return this.state.xAxisFormatter;
  }

  /**
   * Set the format on the y axis items
   * @param {string|Function} value A string with the formatting routine or a function for more customization.
   */
  set yAxisFormatter(value: string | ((value: unknown, data: Array<IdsChartData>, api: this) => string)) {
    this.state.yAxisFormatter = value;
    this.redraw();
  }

  /**
   * Get the y axis formatter
   * @returns {any} the y axis formatter function or configuration
   */
  get yAxisFormatter(): any {
    return this.state.yAxisFormatter;
  }

  /**
   * Reanimate the chart
   */
  reanimate(): void {
    if (!this.animated || !this.initialized) {
      return;
    }

    requestAnimationFrame(() => {
      this.container?.querySelectorAll('animate').forEach((elem: SVGAnimationElement) => {
        if (elem.beginElement) {
          elem.beginElement();
        }
      });
      this.container?.querySelectorAll('animateTransform').forEach((elem: SVGAnimationElement) => {
        if (elem.beginElement) {
          elem.beginElement();
        }
      });
    });
  }

  /**
   * Get a reusable snippet to ease the animation
   * @returns {string} the reusable animation snippet
   */
  get cubicBezier(): string {
    return `calcMode="spline" keyTimes="0; 1" keySplines="0.17, 0.04, 0.03, 0.94" begin="0s" dur="${this.animationSpeed}s"`;
  }

  /**
   * Set the animation on/off
   * @param {boolean} value True if animation is on
   */
  set animated(value: boolean) {
    const animated = stringToBool(this.animated);
    this.setAttribute(attributes.ANIMATED, String(value));
    this.redraw();

    if (animated) {
      this.reanimate();
    }
  }

  /**
   * Get the animation state
   * @returns {boolean} true if animation is enabled
   */
  get animated(): boolean {
    const animated = this.getAttribute(attributes.ANIMATED);
    if (animated === null) {
      return true;
    }
    return stringToBool(this.getAttribute(attributes.ANIMATED));
  }

  /**
   * Set the animation speed in s
   * @param {number} value The speed in s
   */
  set animationSpeed(value: number) {
    this.setAttribute(attributes.ANIMATION_SPEED, String(value));
    if (this.animated) {
      this.reanimate();
    }
  }

  /**
   * Get the animation speed
   * @returns {number} the animation speed in seconds
   */
  get animationSpeed(): number {
    return parseFloat(this.getAttribute(attributes.ANIMATION_SPEED) ?? '') || 0.8;
  }

  /**
   * Set the x axis label alignment between start, middle and end
   * @param {string} value start, middle or end
   */
  set alignXLabels(value: string) {
    this.setAttribute(attributes.ALIGN_X_LABELS, value);
    this.redraw();
  }

  /**
   * Get the x axis label alignment
   * @returns {string} the alignment value (start, middle, end)
   */
  get alignXLabels(): string {
    return this.getAttribute(attributes.ALIGN_X_LABELS) || 'start';
  }

  /**
   * Stack the data forming a stacked bar chart
   * @param {boolean} value True to stack the data
   */
  set stacked(value: boolean) {
    this.setAttribute(attributes.STACKED, String(value));
    this.redraw();
  }

  /**
   * Get the stacked state
   * @returns {boolean} true if data is stacked
   */
  get stacked(): boolean {
    return stringToBool(this.getAttribute(attributes.STACKED)) || false;
  }

  /**
   * Set the bottom axis label text
   * @param {string} value of the text
   */
  set axisLabelBottom(value: string | null) {
    if (value) {
      this.setAttribute(attributes.AXIS_LABEL_BOTTOM, value);
    } else {
      this.removeAttribute(attributes.AXIS_LABEL_BOTTOM);
    }
    this.#setAxisLabels('bottom');
  }

  /**
   * Get the bottom axis label text
   * @returns {string | null} the bottom axis label
   */
  get axisLabelBottom() {
    return this.getAttribute(attributes.AXIS_LABEL_BOTTOM);
  }

  /**
   * Set the end axis label text
   * @param {string} value of the text
   */
  set axisLabelEnd(value: string | null) {
    if (value) {
      this.setAttribute(attributes.AXIS_LABEL_END, value);
    } else {
      this.removeAttribute(attributes.AXIS_LABEL_END);
    }
    this.#setAxisLabels('end');
  }

  /**
   * Get the end axis label text
   * @returns {string | null} the end axis label
   */
  get axisLabelEnd() {
    return this.getAttribute(attributes.AXIS_LABEL_END);
  }

  /**
   * Set the margin for axis label text
   * @param {string} value of the margin
   */
  set axisLabelMargin(value: number) {
    const val = stringToNumber(value);
    if (!Number.isNaN(val)) {
      this.setAttribute(attributes.AXIS_LABEL_MARGIN, String(val));
    } else {
      this.removeAttribute(attributes.AXIS_LABEL_MARGIN);
    }
    this.#setAxisLabels('end');
  }

  /**
   * Get the margin for axis label text
   * @returns {number} the axis label margin
   */
  get axisLabelMargin(): number {
    const value = stringToNumber(this.getAttribute(attributes.AXIS_LABEL_MARGIN));
    return !Number.isNaN(value) ? value : 16;
  }

  /**
   * Set the start axis label text
   * @param {string} value of the text
   */
  set axisLabelStart(value: string | null) {
    if (value) {
      this.setAttribute(attributes.AXIS_LABEL_START, value);
    } else {
      this.removeAttribute(attributes.AXIS_LABEL_START);
    }
    this.#setAxisLabels('start');
  }

  /**
   * Get the start axis label text
   * @returns {string | null} the start axis label
   */
  get axisLabelStart() {
    return this.getAttribute(attributes.AXIS_LABEL_START);
  }

  /**
   * Set the top axis label text
   * @param {string} value of the text
   */
  set axisLabelTop(value: string | null) {
    if (value) {
      this.setAttribute(attributes.AXIS_LABEL_TOP, value);
    } else {
      this.removeAttribute(attributes.AXIS_LABEL_TOP);
    }
    this.#setAxisLabels('top');
  }

  /**
   * Get the top axis label text
   * @returns {string | null} the top axis label
   */
  get axisLabelTop() {
    return this.getAttribute(attributes.AXIS_LABEL_TOP);
  }

  /**
   * Set the rotation for the axis name label text (eg 45deg)
   * @param {number} value the number of degrees to rotate the text
   */
  set rotateNameLabels(value: number) {
    if (value) {
      this.setAttribute(attributes.ROTATE_NAME_LABELS, String(value));
    } else {
      this.removeAttribute(attributes.ROTATE_NAME_LABELS);
    }
    if (this.initialized) this.redraw();
  }

  /**
   * Get the rotation for the axis name label text
   * @returns {number} the number of degrees to rotate the text
   */
  get rotateNameLabels(): number {
    return Number(this.getAttribute(attributes.ROTATE_NAME_LABELS)) || 0;
  }

  /**
   * Enable logarithmic scaling for the chart
   * @param {boolean} value True to enable log scale
   */
  set useLogScale(value: boolean) {
    this.toggleAttribute(attributes.USE_LOG_SCALE, value);
    if (this.initialized) {
      this.redraw();
    }
  }

  /**
   * Get the log scale state
   * @returns {boolean} true if log scale is enabled
   */
  get useLogScale(): boolean {
    return this.hasAttribute(attributes.USE_LOG_SCALE);
  }

  /** override */
  afterCalculateCallback() {
  }

  /** override */
  afterConnectedCallback() {
  }
}
